import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState } from "react";
const apiRoot = apiUrl("/api/sop-sharing/");
const entryKey = e => `${e.dbName}:${e.staffId}`;
const box = {
  background: "white",
  padding: 16,
  border: "1px solid #D9D9D9",
  borderRadius: 8,
  minWidth: 0
};
const button = {
  color: "#262626",
  padding: "7px 12px",
  border: "1px solid #D9D9D9",
  borderRadius: 4,
  cursor: "pointer",
  background: "#FAFAFA",
  margin: 3
};
const field = {
  padding: 8,
  border: "1px solid #D9D9D9",
  borderRadius: 4,
  width: "100%",
  boxSizing: "border-box",
  marginBottom: 8
};
async function api(path, body = {}) {
  const response = await fetch(apiRoot + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("authToken")}`
    },
    body: JSON.stringify(body)
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || "Request failed");
  return json;
}
export default function SOPStaffAssociations({
  onBack,
  masked = false
}) {
  const [data, setData] = useState({
    staff: [],
    groups: [],
    sops: [],
    equivalences: []
  });
  const [selected, setSelected] = useState("");
  const [members, setMembers] = useState([]);
  const [homeKey, setHomeKey] = useState("");
  const [group, setGroup] = useState(null);
  const [search, setSearch] = useState("");
  const [addSearch, setAddSearch] = useState("");
  const [addKey, setAddKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoResult, setUndoResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const name = value => masked ? String(value || "").replace(/[a-z]/g, "x").replace(/[A-Z]/g, "X") : value;
  const label = p => p ? `${name(p.name)} — ${name(p.branchName)}${p.active ? "" : " (inactive)"}` : "Entry unavailable";
  const person = key => data.staff.find(p => p.key === key);
  const branchOf = db => {
    const p = data.staff.find(x => x.dbName === db);
    return name(p ? p.branchName : db);
  };
  const fmt = d => d ? new Date(d).toLocaleString("en-GB") : "—";
  const fmtDay = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  async function startUndo(body, what) {
    setUndoResult(null);
    setUndoPreview(null);
    const p = await api("undo-preview", body);
    setUndoPreview({
      ...p,
      what
    });
    if (!p.eligible) setMessage(`Undo dry run complete: nothing to undo for ${what}.`);
  }
  async function load() {
    const response = await api("list");
    setData(response);
    return response;
  }
  async function perform(action) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    perform(load);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  function choose(key, dataset = data) {
    const linked = dataset.groups.find(g => g.memberKeys.includes(key));
    const p = dataset.staff.find(p => p.key === key);
    setSelected(key);
    setGroup(linked || null);
    setMembers(linked ? linked.members : p ? [{
      dbName: p.dbName,
      staffId: p.staffId
    }] : []);
    setHomeKey(linked ? linked.homeKey : key);
    setPreview(null);
    setAddKey("");
    setMessage("");
  }
  const dirty = selected && (JSON.stringify(members) !== JSON.stringify(group?.members || []) || homeKey !== group?.homeKey);
  const available = data.staff.filter(p => !members.some(m => m.dbName === p.dbName) && !data.groups.some(g => g._id !== group?._id && g.memberKeys.includes(p.key)) && `${p.name} ${p.branchName} ${p.email}`.toLowerCase().includes(addSearch.toLowerCase()));
  return <div style={{
    padding: 20,
    background: "#FAFAFA",
    color: "#262626"
  }}>
    <button style={button} onClick={onBack} disabled={busy}>← SOP Compliance</button>
    <h2>Staff associations and SOP replication</h2>
    <p>Link the same person’s branch entries and choose their regular Home site. An original reading at any linked branch can satisfy the identical SOP at another linked branch.</p>
    {error && <div role="alert" style={{
      ...box,
      color: "#A8071A",
      marginBottom: 12
    }}>{error}</div>}
    {message && <div role="status" style={{
      ...box,
      color: "#389E0D",
      marginBottom: 12
    }}>{message}</div>}
    <fieldset disabled={busy} style={{
      border: 0,
      padding: 0,
      margin: 0
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
        gap: 16
      }}>
        <section style={box}>
          <h3>All staff entries</h3>
          <input aria-label="Search staff" style={field} placeholder="Search name, branch or email" value={search} onChange={e => setSearch(e.target.value)} />
          <div style={{
            maxHeight: 450,
            overflowY: "auto"
          }}>
            {data.staff.filter(p => `${p.name} ${p.branchName} ${p.email}`.toLowerCase().includes(search.toLowerCase())).map(p => <button key={p.key} style={{
              ...button,
              display: "block",
              width: "98%",
              textAlign: "left",
              background: p.key === selected ? "#F6FFED" : "white"
            }} onClick={async () => {
              if (dirty && !(await appConfirm("Discard unsaved association changes?"))) return;
              choose(p.key);
            }}>{label(p)}{data.groups.some(g => g.memberKeys.includes(p.key)) ? " · linked" : ""}</button>)}
          </div>
        </section>
        <section style={box}>
          <h3>Associated branch entries</h3>
          {!selected ? <p>Select a staff member from the left panel.</p> : <>
            <p>Choose exactly one Home site. Saving a link records who made the change.</p>
            <table style={{
              width: "100%",
              borderCollapse: "collapse"
            }}><thead><tr><th align="left">Staff / branch</th><th>Home</th><th /></tr></thead><tbody>
              {members.map(m => <tr key={entryKey(m)}><td style={{
                    padding: 8
                  }}>{label(person(entryKey(m)))}</td><td><input type="radio" name="home-site" aria-label={`Home: ${label(person(entryKey(m)))}`} checked={homeKey === entryKey(m)} onChange={() => setHomeKey(entryKey(m))} /></td><td><button style={button} disabled={members.length === 1} onClick={() => {
                      const next = members.filter(x => entryKey(x) !== entryKey(m));
                      setMembers(next);
                      if (homeKey === entryKey(m)) setHomeKey(entryKey(next[0]));
                    }}>Remove</button></td></tr>)}
            </tbody></table>
            <input aria-label="Search entries to associate" style={field} placeholder="Find another branch entry" value={addSearch} onChange={e => setAddSearch(e.target.value)} />
            <select aria-label="Entry to associate" style={field} value={addKey} onChange={e => setAddKey(e.target.value)}><option value="">Choose staff entry…</option>{available.map(p => <option key={p.key} value={p.key}>{label(p)}</option>)}</select>
            <button style={button} disabled={!addKey} onClick={() => {
              const p = person(addKey);
              if (p) setMembers([...members, {
                dbName: p.dbName,
                staffId: p.staffId
              }]);
              setAddKey("");
            }}>Add association</button>
            <button style={button} disabled={!dirty} onClick={() => perform(async () => {
              await api("save-association", {
                groupId: group?._id,
                revision: group?.revision,
                members,
                homeKey
              });
              const fresh = await load();
              choose(entryKey(members[0]), fresh);
              setMessage("Association saved.");
            })}>Save associations</button>
            {group?.history?.length > 0 && <details><summary>Association history ({group.history.length})</summary>{[...group.history].reverse().map(h => <div key={h.eventId} style={{
                padding: 8,
                borderBottom: "1px solid #eee"
              }}><strong>{h.action}</strong> · {new Date(h.at).toLocaleString("en-GB")} · {name(h.actor)}<div>Home: {label(person(h.after.homeKey))} · {h.after.members.map(m => label(person(entryKey(m)))).join("; ")}</div>{h.before && <div>Previous Home: {label(person(h.before.homeKey))} · {h.before.members.map(m => label(person(entryKey(m)))).join("; ")}</div>}</div>)}</details>}
          </>}
        </section>

      </div>
      <section style={{
        ...box,
        marginTop: 16
      }}>
        <h3>Replicate SOP readings</h3>
        <p>SOPs match automatically when their document names and versions match, ignoring case and extra spaces. Branch document hashes are ignored. If both versions are missing, they are treated as matching; a missing version does not match a populated version.</p>
        <p>This processes all saved associated people across your organisation. It fills existing unread assignments only, dated with the day the person actually read the SOP. Unassigned SOPs stay unassigned; replicated readings cannot act as original sources. Copied readings are marked ↻ on the SOP grid, and any copy can be undone from the replication log.</p>
        <button style={button} disabled={!!dirty} onClick={() => perform(async () => {
          setPreview(null);
          setResult(null);
          const proposed = await api("preview");
          setPreview(proposed);
          if (!proposed.eligible) setMessage("Dry run complete: no eligible changes. Check SOP names, versions and original readings.");
        })}>Dry run</button>
        <button style={{
          ...button,
          background: "#F6FFED"
        }} disabled={!!dirty || !preview?.eligible || !preview?.previewToken} onClick={() => perform(async () => {
          const reviewed = preview;
          setPreview(null);
          setResult(await api("replicate", {
            previewToken: reviewed.previewToken
          }));
          setHistory(await api("history"));
        })}>Commit{preview?.eligible ? ` ${preview.eligible} change${preview.eligible === 1 ? "" : "s"}` : " changes"}</button>
        <button style={button} onClick={() => perform(async () => setHistory(await api("history")))}>View replication log</button>
        {dirty && <p>Save or discard your association changes before replication.</p>}
        {preview && <>
          <h4>Dry run — proposed changes</h4>
          <p>No SOP readings have been changed. {preview.eligible} changes proposed · {preview.alreadyRead} already read · {preview.unmatched} unread assignments without a matching original reading.</p>
          <p>Review the rows below, then click Commit. This dry run expires at {new Date(preview.expiresAt).toLocaleTimeString("en-GB")}. If the underlying records change, a fresh dry run is required.</p>
          <div style={{
            overflowX: "auto"
          }}><table style={{
              width: "100%",
              fontSize: 13,
              textAlign: "left",
              borderCollapse: "collapse"
            }}>
            <thead><tr><th>Staff member</th><th>SOP</th><th>Read at</th><th>Original reading (date copied across)</th><th>Update branch</th><th>Current</th><th>After commit</th></tr></thead>
            <tbody>{preview.candidates.map((c, i) => <tr key={c.targetAssignmentId || i}>
              <td style={{
                    padding: 8
                  }}>{name(c.person)}</td><td style={{
                    padding: 8
                  }}>{c.sop}</td>
              <td style={{
                    padding: 8
                  }}>{name(c.sourceBranch)} · {name(c.sourcePerson)}</td>
              <td style={{
                    padding: 8
                  }}>{new Date(c.originalReadAt).toLocaleString("en-GB")}</td>
              <td style={{
                    padding: 8
                  }}>{name(c.targetBranch)}</td><td style={{
                    padding: 8
                  }}>{c.currentStatus}</td><td style={{
                    padding: 8
                  }}>{c.proposedStatus}</td>
            </tr>)}</tbody>
          </table></div>
          <details><summary>Unmatched assignments ({preview.unmatched})</summary>{preview.unmatchedItems.map((c, i) => <div key={i}>{name(c.person)} · {c.sop} · {name(c.branch)}</div>)}</details>
        </>}

        {result && <div role="status"><p>{result.updated} replicated · {result.raced} changed during processing · {result.recovered} interrupted log entries reconciled · {result.failures.length} errors.</p>{result.failures.map((f, i) => <p key={i}>{name(f.person)} · {f.sop}: {f.error}</p>)}</div>}
        {history && <>
          <h4>Runs (latest 30)</h4>
          <div style={{
            overflowX: "auto"
          }}><table style={{
              width: "100%",
              fontSize: 12,
              borderCollapse: "collapse"
            }}>
            <thead><tr><th align="left">When</th><th align="left">Type</th><th align="left">By</th><th align="left">Status</th><th align="left">Result</th><th /></tr></thead>
            <tbody>{history.runs.map(r => <tr key={r._id} style={{
                  borderTop: "1px solid #eee"
                }}>
              <td style={{
                    padding: 6
                  }}>{fmt(r.createdAt)}</td>
              <td style={{
                    padding: 6
                  }}>{r.type === "undo" ? "Undo" : "Replication"}</td>
              <td style={{
                    padding: 6
                  }}>{name(r.actor)}</td>
              <td style={{
                    padding: 6
                  }}>{r.status}{r.error ? ` · ${r.error}` : ""}</td>
              <td style={{
                    padding: 6
                  }}>{r.type === "undo" ? `${r.result?.undone || 0} undone${r.result?.skipped ? ` · ${r.result.skipped} skipped` : ""}` : `${r.result?.updated || 0} replicated`}</td>
              <td style={{
                    padding: 6
                  }}>{r.type !== "undo" && (r.result?.updated || 0) > 0 && <button style={button} onClick={() => perform(() => startUndo({
                      runId: r.runId
                    }, `the run of ${fmt(r.createdAt)}`))}>Undo this run…</button>}</td>
            </tr>)}</tbody>
          </table></div>
          <h4>Copied readings (latest 200)</h4>
          <p style={{
            fontSize: 12,
            color: "#4b5563"
          }}>Each copy is kept here permanently, with who did it and when. “Undo” puts that reading back to unread at the destination branch. It is always a dry run first, and a copy the person has since read for real is left alone.</p>
          <div style={{
            overflowX: "auto"
          }}><table style={{
              width: "100%",
              fontSize: 12,
              borderCollapse: "collapse"
            }}>
            <thead><tr><th align="left">Staff / SOP</th><th align="left">Read at → copied to</th><th align="left">Original read</th><th align="left">Copied</th><th align="left">By</th><th align="left">Status</th><th /></tr></thead>
            <tbody>{history.replications.map(r => <tr key={r.operationId} style={{
                  borderTop: "1px solid #eee",
                  background: r.status === "undone" ? "#FAFAFA" : undefined,
                  color: r.status === "undone" ? "#5D5D5D" : undefined
                }}>
              <td style={{
                    padding: 6
                  }}>{name(r.target.staffName)} · {r.target.sopTitle}</td>
              <td style={{
                    padding: 6
                  }}>{r.source.branchName ? name(r.source.branchName) : branchOf(r.source.dbName)} → {r.target.branchName ? name(r.target.branchName) : branchOf(r.target.dbName)}</td>
              <td style={{
                    padding: 6
                  }}>{fmtDay(r.originalReadAt)}</td>
              <td style={{
                    padding: 6
                  }}>{fmt(r.replicatedAt)}</td>
              <td style={{
                    padding: 6
                  }}>{name(r.replicatedBy)}</td>
              <td style={{
                    padding: 6
                  }}>{r.status === "undone" ? `Undone ${fmt(r.undoneAt)} by ${name(r.undoneBy)}` : r.status === "applied" ? "Applied" : r.status}</td>
              <td style={{
                    padding: 6
                  }}>{r.status === "applied" && <button style={button} onClick={() => perform(() => startUndo({
                      operationIds: [r.operationId]
                    }, `${r.target.staffName} · ${r.target.sopTitle}`))}>Undo…</button>}</td>
            </tr>)}</tbody>
          </table></div>
        </>}
        {undoPreview && <div style={{
          ...box,
          marginTop: 12,
          borderColor: "#FA8C16",
          background: "#FFFBE6"
        }}>
          <h4 style={{
            marginTop: 0
          }}>Undo dry run — {undoPreview.what}</h4>
          <p>Nothing has been changed yet. {undoPreview.eligible} reading{undoPreview.eligible === 1 ? "" : "s"} would go back to unread · {undoPreview.skippedCount} left as they are. This dry run expires at {new Date(undoPreview.expiresAt).toLocaleTimeString("en-GB")}.</p>
          <p style={{
            fontSize: 12
          }}>After an undo, the next replication dry run may propose the same copy again. If the copy was wrong because of an association, fix the association first.</p>
          {undoPreview.candidates.length > 0 && <div style={{
            overflowX: "auto"
          }}><table style={{
              width: "100%",
              fontSize: 12,
              borderCollapse: "collapse"
            }}>
            <thead><tr><th align="left">Staff member</th><th align="left">SOP</th><th align="left">Branch</th><th align="left">Original read</th><th align="left">Copied</th><th align="left">Now</th><th align="left">After undo</th></tr></thead>
            <tbody>{undoPreview.candidates.map(c => <tr key={c.operationId} style={{
                  borderTop: "1px solid #FFE58F"
                }}>
              <td style={{
                    padding: 6
                  }}>{name(c.person)}</td><td style={{
                    padding: 6
                  }}>{c.sop}</td><td style={{
                    padding: 6
                  }}>{name(c.targetBranch)}</td>
              <td style={{
                    padding: 6
                  }}>{fmtDay(c.originalReadAt)}</td><td style={{
                    padding: 6
                  }}>{fmt(c.replicatedAt)}</td><td style={{
                    padding: 6
                  }}>{c.currentStatus}</td><td style={{
                    padding: 6
                  }}>{c.proposedStatus}</td>
            </tr>)}</tbody>
          </table></div>}
          {undoPreview.skipped.length > 0 && <details><summary>Left as they are ({undoPreview.skipped.length})</summary>{undoPreview.skipped.map((k, i) => <div key={i} style={{
              fontSize: 12
            }}>{name(k.person)} · {k.sop} · {name(k.targetBranch)} — {k.reason}</div>)}</details>}
          <div style={{
            marginTop: 8
          }}>
            <button style={{
              ...button,
              background: "#FFE58F"
            }} disabled={!undoPreview.eligible} onClick={() => perform(async () => {
              const reviewed = undoPreview;
              setUndoPreview(null);
              setUndoResult(await api("undo", {
                previewToken: reviewed.previewToken
              }));
              setHistory(await api("history"));
            })}>Apply undo{undoPreview.eligible ? ` (${undoPreview.eligible})` : ""}</button>
            <button style={button} onClick={() => setUndoPreview(null)}>Cancel</button>
          </div>
        </div>}
        {undoResult && <div role="status" style={{
          ...box,
          marginTop: 12
        }}>{undoResult.undone} reading{undoResult.undone === 1 ? "" : "s"} put back to unread · {undoResult.raced} changed during processing · {undoResult.skipped} left as they were{undoResult.recovered ? ` · ${undoResult.recovered} interrupted undos reconciled` : ""} · {undoResult.failures.length} errors.{undoResult.failures.map((f, i) => <div key={i}>{name(f.person)} · {f.sop}: {f.error}</div>)}</div>}
      </section>
    </fieldset>
    {busy && <p role="status">Working…</p>}
  </div>;
}
